/**
 * Contrato de las imágenes del carrusel del home (hero) del sitio público.
 *
 * El frontend NO consulta al backend por estas imágenes: arma la URL por convención y la
 * pide directo al bucket. Ver `frontend/src/components/home/OptimizedHero.tsx` →
 * `getImagePath(windowWidth, imageNumber)`. O sea: los nombres de archivo y los cuatro
 * tamaños de acá son un contrato con ese archivo. Si cambia uno, cambia el otro.
 *
 * Ruta: portada/{sizeS|sizeM|sizeL|sizeXL}/{portada|portada1|portada2}.webp  → 12 objetos.
 *
 * Los ANCHOS DE CORTE (500 / 1270 / 1500) los decide el frontend por `window.innerWidth`.
 * Las medidas de salida de acá salen de medir la caja real del hero en cada tramo:
 * el alto es `clamp(250px, 65vw, 400px)` en celular y `clamp(280px, 40vw, 550px)` de 640px
 * para arriba, así que la relación de aspecto pasa de ~1.5:1 en celular a ~3.5:1 en monitor.
 * Por eso son cuatro recortes distintos y no cuatro escalas de la misma imagen.
 */

/** Carpeta raíz en Storage. */
export const PORTADA_PATH = 'portada';

/**
 * Las cuatro medidas, en el orden en que se muestran en pantalla.
 * `width`/`height` son el objetivo; si el original no da, se genera más chico con la misma
 * relación de aspecto (ver `renderCoverBlob`). `viewport` es solo para explicarlo en la UI.
 *
 * OJO CON `caja*`: el archivo NO se muestra entero. El hero es `width:100%` con alto
 * `clamp(...)`, así que la forma de la caja **cambia dentro de cada tramo**, y el `<img>` es
 * `object-cover object-center` → el navegador recorta el archivo OTRA VEZ, centrado y sin
 * saber nada del punto focal. `cajaMin`/`cajaMax` son las relaciones de aspecto extremas que
 * puede tomar la caja en ese tramo; con eso la pantalla calcula la zona que sobrevive DE
 * VERDAD, en vez de prometer la forma del archivo. Cuánto recorta de más, medido:
 *
 *   viewport 500  → caja 1.54 vs archivo 2.00 → se pierde 23% del ancho
 *   viewport 768  → caja 2.50 vs archivo 2.00 → se pierde 20% del alto
 *   viewport 2560 → caja 4.65 vs archivo 3.51 → se pierde 25% del alto
 *
 * `muestra` es un dispositivo representativo del tramo, para el preview.
 */
export const PORTADA_SIZES = [
  {
    key: 'sizeS', label: 'Celular', viewport: 'menos de 500px', width: 1200, height: 780,
    cajaMin: 320 / 250, cajaMax: 499 / 324, muestra: { label: 'celular de 390px', ratio: 390 / 254 },
  },
  {
    key: 'sizeM', label: 'Celular grande / tablet', viewport: '500 a 1269px', width: 1600, height: 800,
    // El salto de 1.60 a 2.29 es el breakpoint `sm:` de Tailwind (640px), donde cambia la
    // fórmula del alto. Por eso este tramo es el que más recorta de más.
    cajaMin: 500 / 325, cajaMax: 1269 / 507.6, muestra: { label: 'tablet de 820px', ratio: 820 / 328 },
  },
  {
    key: 'sizeL', label: 'Notebook', viewport: '1270 a 1499px', width: 1920, height: 740,
    cajaMin: 1270 / 508, cajaMax: 1499 / 550, muestra: { label: 'notebook de 1440px', ratio: 1440 / 550 },
  },
  {
    key: 'sizeXL', label: 'Monitor grande', viewport: '1500px o más', width: 2560, height: 730,
    // El alto se clava en 550, así que la caja se estira sin techo con el ancho del monitor.
    // Se toma 2560 como máximo razonable: garantizar hasta un ultrawide de 3440 (6.25) achica
    // la zona segura para todo el mundo por una pantalla que casi nadie tiene.
    cajaMin: 1500 / 550, cajaMax: 2560 / 550, muestra: { label: 'monitor de 1920px', ratio: 1920 / 550 },
  },
];

/**
 * Los tres slides del carrusel, en el orden en que rotan.
 * `file` es el nombre que espera el frontend; `titulo`/`subtitulo` son los textos que el
 * sitio dibuja ENCIMA de la imagen (por eso la imagen no debe traer texto adentro), y se
 * usan en el preview para mostrar qué parte de la foto queda tapada.
 */
export const PORTADA_SLIDES = [
  {
    key: 'evento',
    file: 'portada.webp',
    nombre: 'Evento destacado',
    ayuda: 'Se muestra solo si hay un evento próximo publicado y el módulo Eventos está activo.',
    titulo: 'Nombre del próximo evento',
    subtitulo: '12 de agosto — Conseguí tus entradas',
    boton: 'Ver evento',
    align: 'left',
    overlay: 'linear-gradient(to right, rgba(88,28,135,.8), rgba(0,0,0,.3))',
  },
  {
    key: 'reservas',
    file: 'portada2.webp',
    nombre: 'Reservá tu mesa',
    ayuda: 'Se muestra si el módulo Reservas está activo. El texto queda a la DERECHA.',
    titulo: 'Reservá tu mesa',
    subtitulo: 'Vení a jugar con amigos. Elegí fecha, turno y juego.',
    boton: 'Reservar ahora',
    align: 'right',
    overlay: 'linear-gradient(to right, rgba(49,46,129,.8), rgba(0,0,0,.3))',
  },
  {
    key: 'novedades',
    file: 'portada1.webp',
    nombre: 'Nuevos ingresos',
    ayuda: 'Siempre visible: es el slide que queda cuando los otros dos están apagados.',
    titulo: 'Nuevos ingresos cada semana',
    subtitulo: 'Mantente actualizado con las últimas novedades',
    boton: 'Ver novedades',
    align: 'left',
    overlay: 'linear-gradient(to right, rgba(0,0,0,.7), rgba(0,0,0,.3))',
  },
];

/**
 * Arriba de esto la imagen se marca como "pesada". Una portada bien generada da entre 100 y
 * 150 KB; el techo es holgado a propósito, para señalar sólo lo que de verdad duele en un
 * celular (la que motivó todo esto pesaba 12,3 MB).
 */
export const PESO_MAXIMO = 500 * 1024;

/**
 * Para llenar una medida sin agrandar hacen falta LAS DOS dimensiones: recortar "cover" a
 * WxH necesita `ancho >= W` y `alto >= H`. Mirar solo el ancho deja pasar panorámicas
 * (3000x400 cumple "2560 de ancho" y sin embargo sale más chica en las cuatro medidas).
 */
/** Con esto se llenan las cuatro medidas, incluida la de monitor (2560x730). */
export const ANCHO_RECOMENDADO = 2560;
export const ALTO_RECOMENDADO = 800;
/** Mínimo aceptado: lo que necesita la medida de celular (1200x780). Menos que esto es borroso. */
export const ANCHO_MINIMO = 1200;
export const ALTO_MINIMO = 780;

/** Bucket configurado para este build. Los .env traen espacios al final: por eso el trim. */
export const bucketDePortada = () =>
  String(process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '').trim();

/**
 * URL pública de un archivo de portada, la misma que arma el frontend.
 * `bust` agrega un parámetro para saltear el caché del navegador (las imágenes se sirven
 * con `max-age=3600`, así que sin esto el admin no ve lo que acaba de subir).
 */
export const portadaUrl = (sizeKey, file, bust) => {
  const base = `https://storage.googleapis.com/${bucketDePortada()}/${PORTADA_PATH}/${sizeKey}/${file}`;
  return bust ? `${base}?v=${bust}` : base;
};

/** "1,2 MB" / "340 KB" — para mostrar pesos sin marear. */
export const formatoPeso = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
